import { useState, useCallback, useMemo } from "react";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { SortableHeaderCell } from "components/common/SortableHeaderCell";
import { useTenantTranslation } from "hooks/formatters";
import { useTenant } from "hooks/settings";
import { useTableSort, type SortValueGetters } from "hooks/useTableSort";
import { useNotifyStatus } from "contexts/status";
import type {
  JobFailureWindow,
  TerminalFailuresByModel,
} from "src/store/api/generatedApi";
import {
  usePreviewTerminalFailuresQuery,
  useResetTerminalFailuresMutation,
} from "src/store/api/generatedApi";
import dayjs from "dayjs";
import { useResolvedScrapViewportTimeZone } from "store/slices/scrapTrackingViewportSlice";

type ResetTerminalFailuresDialogProps = Readonly<{
  open: boolean;
  onClose: () => void;
  clickedWindow: JobFailureWindow | null;
  targetLocation: string;
  enabledAngleNames: string[];
  selectedLabellingName: string | null;
  viewportStart: string;
  viewportEnd: string;
}>;

type TerminalFailuresModelColumn = "labellingName" | "jobType" | "count";

const terminalFailuresModelSortValueGetters: SortValueGetters<
  TerminalFailuresByModel,
  TerminalFailuresModelColumn
> = {
  labellingName: (model) => model.labelling_name,
  jobType: (model) => model.job_type,
  count: (model) => model.count,
};

const TerminalFailuresByModelTable = ({
  models,
}: Readonly<{ models: readonly TerminalFailuresByModel[] }>) => {
  const { t } = useTenantTranslation();
  const { sortedRows, sort, toggleSort } = useTableSort({
    rows: models,
    sortValueGetters: terminalFailuresModelSortValueGetters,
    initialSort: null,
  });

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <SortableHeaderCell
            column="labellingName"
            sort={sort}
            onSort={toggleSort}
          >
            {t("model")}
          </SortableHeaderCell>
          <SortableHeaderCell column="jobType" sort={sort} onSort={toggleSort}>
            {t("resetTerminalFailuresJobType")}
          </SortableHeaderCell>
          <SortableHeaderCell
            column="count"
            sort={sort}
            onSort={toggleSort}
            align="right"
          >
            {t("modelPerformanceCount")}
          </SortableHeaderCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {sortedRows.map((model) => (
          <TableRow key={`${model.labelling_name}-${model.job_type}`}>
            <TableCell>{model.labelling_name}</TableCell>
            <TableCell>{model.job_type}</TableCell>
            <TableCell align="right">{model.count}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export const ResetTerminalFailuresDialog = ({
  open,
  onClose,
  clickedWindow,
  targetLocation,
  enabledAngleNames,
  selectedLabellingName,
  viewportStart,
  viewportEnd,
}: ResetTerminalFailuresDialogProps) => {
  const { t } = useTenantTranslation();
  const tenant = useTenant();
  const notifyStatus = useNotifyStatus();
  const timeZone = useResolvedScrapViewportTimeZone();
  const [resetting, setResetting] = useState(false);

  const windowStart = clickedWindow?.window_start;
  const windowEnd = clickedWindow?.window_end;

  const anglesJson = useMemo(
    () =>
      enabledAngleNames.length > 0
        ? JSON.stringify(enabledAngleNames)
        : undefined,
    [enabledAngleNames]
  );
  const labellingName = selectedLabellingName ?? undefined;

  const {
    data: windowPreview,
    isLoading: windowPreviewLoading,
    isError: windowPreviewError,
  } = usePreviewTerminalFailuresQuery(
    {
      tenantName: tenant,
      targetLocation,
      start: windowStart,
      end: windowEnd,
      anglesJson,
      labellingName,
    },
    { skip: !open || !targetLocation || !windowStart || !windowEnd }
  );

  const {
    data: visiblePreview,
    isLoading: visiblePreviewLoading,
    isError: visiblePreviewError,
  } = usePreviewTerminalFailuresQuery(
    {
      tenantName: tenant,
      targetLocation,
      start: viewportStart,
      end: viewportEnd,
      anglesJson,
      labellingName,
    },
    { skip: !open || !targetLocation }
  );

  const [resetTerminalFailures] = useResetTerminalFailuresMutation();

  const windowCount = windowPreview?.total_count ?? 0;
  const visibleCount = visiblePreview?.total_count ?? 0;
  const previewLoading = windowPreviewLoading || visiblePreviewLoading;
  const previewError = windowPreviewError || visiblePreviewError;

  const handleReset = useCallback(
    async (scope: "window" | "visible") => {
      setResetting(true);
      try {
        const args =
          scope === "window" && windowStart && windowEnd
            ? {
                tenantName: tenant,
                targetLocation,
                start: windowStart,
                end: windowEnd,
                anglesJson,
                labellingName,
              }
            : {
                tenantName: tenant,
                targetLocation,
                start: viewportStart,
                end: viewportEnd,
                anglesJson,
                labellingName,
              };

        const result = await resetTerminalFailures(args).unwrap();
        notifyStatus({
          type: "success",
          text: t("resetTerminalFailuresSuccess", {
            count: result.batches_reset,
          }),
        });
        onClose();
      } catch {
        notifyStatus({
          type: "error",
          text: t("resetTerminalFailuresError"),
        });
      } finally {
        setResetting(false);
      }
    },
    [
      resetTerminalFailures,
      tenant,
      targetLocation,
      windowStart,
      windowEnd,
      viewportStart,
      viewportEnd,
      anglesJson,
      labellingName,
      notifyStatus,
      t,
      onClose,
    ]
  );

  const windowInfo = clickedWindow
    ? t("resetTerminalFailuresWindowInfo", {
        start: dayjs(clickedWindow.window_start)
          .tz(timeZone)
          .format("YYYY-MM-DD HH:mm"),
        end: dayjs(clickedWindow.window_end)
          .tz(timeZone)
          .format("YYYY-MM-DD HH:mm"),
        terminalCount: clickedWindow.terminal_failure_count,
        retriableCount: clickedWindow.retriable_failure_count,
      })
    : "";

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t("resetTerminalFailuresTitle")}</DialogTitle>

      <DialogContent>
        <Stack spacing={2}>
          {clickedWindow && <Alert severity="info">{windowInfo}</Alert>}

          <Divider />

          {previewLoading && (
            <Stack alignItems="center" py={2}>
              <CircularProgress size={24} />
            </Stack>
          )}

          {previewError && (
            <Alert severity="error">
              {t("resetTerminalFailuresPreviewError")}
            </Alert>
          )}

          {!previewLoading && !previewError && windowCount === 0 && (
            <Typography>{t("resetTerminalFailuresNoFailures")}</Typography>
          )}

          {!previewLoading && !previewError && windowCount > 0 && (
            <>
              <Alert severity="warning">
                {t("resetTerminalFailuresWindowWarning", {
                  windowCount,
                  visibleCount,
                  location: targetLocation,
                })}
              </Alert>

              {windowPreview && (
                <TerminalFailuresByModelTable models={windowPreview.by_model} />
              )}
            </>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button color="secondary" onClick={onClose}>
          {t("cancel")}
        </Button>

        <LoadingButton
          variant="text"
          color="warning"
          loading={resetting}
          disabled={visibleCount === 0 || previewLoading || previewError}
          onClick={() => void handleReset("visible")}
        >
          {t("resetTerminalFailuresAllVisible", { count: visibleCount })}
        </LoadingButton>

        <LoadingButton
          variant="contained"
          color="warning"
          loading={resetting}
          disabled={windowCount === 0 || previewLoading || previewError}
          onClick={() => void handleReset("window")}
        >
          {t("resetTerminalFailuresWindow", { count: windowCount })}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
